'use client';

import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Card, 
  CardContent, 
  Chip, 
  Button,
  Fade,
  Zoom,
  Paper,
  Stack
} from '@mui/material';
import CheckCircle from '@/letsgo/icons/CheckCircle';
import ArrowForward from '@/letsgo/icons/ArrowForward';
import { PlatformIcon } from '@/components/ConnectedAccountCard';

const PLATFORM_ICON_SIZE = 40;

const PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: <PlatformIcon platformId="instagram" size={PLATFORM_ICON_SIZE} />, color: '#E4405F', features: ['Stories', 'Reels', 'Posts', 'IGTV', 'Shopping'], description: 'Visual content and stories' },
  { id: 'youtube', name: 'YouTube', icon: <PlatformIcon platformId="youtube" size={PLATFORM_ICON_SIZE} />, color: '#FF0000', features: ['Videos', 'Shorts', 'Live', 'Community', 'Monetization'], description: 'Video content and monetization' },
  { id: 'tiktok', name: 'TikTok', icon: <PlatformIcon platformId="tiktok" size={PLATFORM_ICON_SIZE} />, color: '#000000', features: ['Short Videos', 'Trends', 'Effects', 'Live', 'Ads'], description: 'Short-form video content' },
  { id: 'twitter', name: 'X (Twitter)', icon: <PlatformIcon platformId="twitter" size={PLATFORM_ICON_SIZE} />, color: '#000000', features: ['Tweets', 'Threads', 'Spaces', 'Fleets', 'Analytics'], description: 'Real-time updates and engagement' },
  { id: 'facebook', name: 'Facebook', icon: <PlatformIcon platformId="facebook" size={PLATFORM_ICON_SIZE} />, color: '#1877F2', features: ['Posts', 'Stories', 'Groups', 'Events', 'Marketplace'], description: 'Community and business pages' },
  { id: 'linkedin', name: 'LinkedIn', icon: <PlatformIcon platformId="linkedin" size={PLATFORM_ICON_SIZE} />, color: '#0A66C2', features: ['Articles', 'Posts', 'Videos', 'Events', 'Networking'], description: 'Professional networking' },
  { id: 'discord', name: 'Discord', icon: <PlatformIcon platformId="discord" size={PLATFORM_ICON_SIZE} />, color: '#5865F2', features: ['Servers', 'Channels', 'Voice', 'Streaming', 'Bots'], description: 'Community and communication' },
  { id: 'twitch', name: 'Twitch', icon: <PlatformIcon platformId="twitch" size={PLATFORM_ICON_SIZE} />, color: '#9146FF', features: ['Live Streaming', 'Clips', 'VODs', 'Chat', 'Monetization'], description: 'Live streaming platform' },
  { id: 'vimeo', name: 'Vimeo', icon: <PlatformIcon platformId="vimeo" size={PLATFORM_ICON_SIZE} />, color: '#1AB7EA', features: ['HD Videos', 'Live', 'Portfolio', 'Analytics', 'Monetization'], description: 'High-quality video hosting' },
  { id: 'dribbble', name: 'Dribbble', icon: <PlatformIcon platformId="dribbble" size={PLATFORM_ICON_SIZE} />, color: '#EA4C89', features: ['Shots', 'Portfolio', 'Jobs', 'Teams', 'Pro'], description: 'Design inspiration and portfolio' },
  { id: 'slack', name: 'Slack', icon: <PlatformIcon platformId="slack" size={PLATFORM_ICON_SIZE} />, color: '#4A154B', features: ['Channels', 'DMs', 'Files', 'Integrations', 'Workflows'], description: 'Team communication' },
  { id: 'reddit', name: 'Reddit', icon: <PlatformIcon platformId="reddit" size={PLATFORM_ICON_SIZE} />, color: '#FF4500', features: ['Posts', 'Comments', 'Awards', 'Live', 'Communities'], description: 'Community discussions' },
  { id: 'snapchat', name: 'Snapchat', icon: <PlatformIcon platformId="snapchat" size={PLATFORM_ICON_SIZE} />, color: '#FFFC00', features: ['Snaps', 'Stories', 'Lenses', 'Spotlight', 'Maps'], description: 'Ephemeral content' },
  { id: 'github', name: 'GitHub', icon: <PlatformIcon platformId="github" size={PLATFORM_ICON_SIZE} />, color: '#181717', features: ['Repositories', 'Issues', 'Pull Requests', 'Actions', 'Pages'], description: 'Code collaboration' },
  { id: 'whatsapp', name: 'WhatsApp', icon: <PlatformIcon platformId="whatsapp" size={PLATFORM_ICON_SIZE} />, color: '#25D366', features: ['Messages', 'Calls', 'Status', 'Groups', 'Business'], description: 'Messaging and calls' },
  { id: 'mastodon', name: 'Mastodon', icon: <PlatformIcon platformId="mastodon" size={PLATFORM_ICON_SIZE} />, color: '#6364FF', features: ['Toots', 'Boosts', 'Favorites', 'Lists', 'Federated'], description: 'Decentralized social network' }
];

const FEATURES_BY_PLATFORM = {
  'instagram': ['Visual Content', 'Stories', 'Reels', 'Shopping', 'Analytics'],
  'youtube': ['Video Upload', 'Live Streaming', 'Monetization', 'Community', 'Analytics'],
  'tiktok': ['Short Videos', 'Trends', 'Effects', 'Live', 'Ads'],
  'twitter': ['Tweets', 'Threads', 'Spaces', 'Analytics', 'Engagement'],
  'facebook': ['Posts', 'Stories', 'Groups', 'Events', 'Ads'],
  'linkedin': ['Articles', 'Posts', 'Networking', 'Jobs', 'Analytics'],
  'discord': ['Servers', 'Voice Chat', 'Streaming', 'Bots', 'Moderation'],
  'twitch': ['Live Streaming', 'Clips', 'Monetization', 'Chat', 'Analytics'],
  'vimeo': ['HD Videos', 'Portfolio', 'Live', 'Analytics', 'Monetization'],
  'dribbble': ['Portfolio', 'Inspiration', 'Jobs', 'Teams', 'Pro Tools'],
  'slack': ['Channels', 'Integrations', 'Workflows', 'Files', 'Calls'],
  'reddit': ['Communities', 'Discussions', 'Awards', 'Live', 'Moderation'],
  'snapchat': ['Snaps', 'Lenses', 'Stories', 'Maps', 'Spotlight'],
  'github': ['Repositories', 'Collaboration', 'Actions', 'Pages', 'Issues'],
  'whatsapp': ['Messages', 'Calls', 'Groups', 'Business', 'Status'],
  'mastodon': ['Toots', 'Federated', 'Lists', 'Moderation', 'Privacy']
};

export function PlatformSelector() {
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [showFeatures, setShowFeatures] = useState(false);

  const handlePlatformToggle = (platformId: string) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId) 
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    );
  };

  const getRelevantFeatures = () => {
    const allFeatures = selectedPlatforms.flatMap(platformId => 
      FEATURES_BY_PLATFORM[platformId as keyof typeof FEATURES_BY_PLATFORM] || []
    );
    return [...new Set(allFeatures)]; // Remove duplicates
  };

  const relevantFeatures = getRelevantFeatures();

  return (
    <Box
      sx={{
        py: 8,
        width: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        bgcolor: 'background.paper',
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          width: '100%',
          px: { xs: 2, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        <Stack spacing={4} sx={{ width: '100%', mb: 6 }}>
          <Stack spacing={2} alignItems="center" sx={{ width: '100%', textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Choose Your Platforms
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 720 }}>
              Select the platforms you use to see relevant FloAI features
            </Typography>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              width: '100%',
              gap: 2,
              alignItems: 'stretch',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            }}
          >
          {PLATFORMS.map((platform) => (
              <Card
                key={platform.id}
                sx={{
                  width: '100%',
                  minHeight: 188,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease, border-width 0.3s ease',
                  border: selectedPlatforms.includes(platform.id) ? 2 : 1,
                  borderColor: selectedPlatforms.includes(platform.id) 
                    ? platform.color 
                    : 'divider',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4,
                    borderColor: platform.color
                  }
                }}
                onClick={() => handlePlatformToggle(platform.id)}
              >
                <CardContent
                  sx={{
                    p: 2.5,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 1.5,
                    bgcolor: selectedPlatforms.includes(platform.id) ? `${platform.color}10` : 'transparent',
                  }}
                >
                  <Box
                    sx={{
                      color: platform.color,
                      flexShrink: 0,
                      '& .MuiSvgIcon-root': { fontSize: '2rem' },
                    }}
                  >
                    {platform.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'text.primary', lineHeight: 1.3 }}>
                      {platform.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45, mt: 0.5, flex: 1 }}>
                      {platform.description}
                    </Typography>
                  </Box>
                  <Box sx={{ minHeight: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {selectedPlatforms.includes(platform.id) ? (
                      <CheckCircle sx={{ color: 'success.main', fontSize: '1.5rem', flexShrink: 0 }} />
                    ) : null}
                  </Box>
                </CardContent>
              </Card>
          ))}
          </Box>
        </Stack>

        {/* Selected Platforms Summary */}
        {selectedPlatforms.length > 0 && (
          <Fade in={selectedPlatforms.length > 0} timeout={500}>
            <Paper sx={{ p: 4, mb: 4, bgcolor: 'primary.50' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                Selected Platforms ({selectedPlatforms.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                {selectedPlatforms.map(platformId => {
                  const platform = PLATFORMS.find(p => p.id === platformId);
                  return platform ? (
                    <Chip
                      key={platformId}
                      icon={platform.icon}
                      label={platform.name}
                      onDelete={() => handlePlatformToggle(platformId)}
                      sx={{
                        bgcolor: `${platform.color}20`,
                        color: platform.color,
                        fontWeight: 'bold'
                      }}
                    />
                  ) : null;
                })}
              </Box>
              <Button
                variant="contained"
                onClick={() => setShowFeatures(!showFeatures)}
                endIcon={<ArrowForward />}
                sx={(theme) => ({
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                  '&:hover': {
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark || theme.palette.primary.dark} 90%)`
                  }
                })}
              >
                {showFeatures ? 'Hide' : 'Show'} Relevant Features
              </Button>
            </Paper>
          </Fade>
        )}

        {/* Relevant Features */}
        {showFeatures && relevantFeatures.length > 0 && (
          <Zoom in={showFeatures} timeout={500}>
            <Paper sx={{ p: 4, bgcolor: 'success.50' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3 }}>
                FloAI Features for Your Platforms
              </Typography>
              <Stack spacing={2} sx={{ width: '100%' }}>
                {relevantFeatures.map((feature, index) => (
                    <Chip
                      key={index}
                      icon={<CheckCircle />}
                      label={feature}
                      color="success"
                      variant="outlined"
                      sx={{ 
                        width: '100%',
                        fontWeight: 600,
                        height: 'auto',
                        minHeight: 40,
                        py: 0.75,
                        '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left', lineHeight: 1.35 },
                      }}
                    />
                ))}
              </Stack>
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Ready to manage all {selectedPlatforms.length} platforms from one dashboard?
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={(theme) => ({
                    py: 2,
                    px: 4,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                    '&:hover': {
                      background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark || theme.palette.primary.dark} 90%)`,
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  })}
                >
                  Start Free Trial
                </Button>
              </Box>
            </Paper>
          </Zoom>
        )}

      </Container>
    </Box>
  );
}
